<app-nav></app-nav>

<main class="terms-page">
  <!-- ─── Hero ─────────────────────────────────────────────────────── -->
  <section class="legal-hero brand-section">
    <div class="hero-orb hero-orb-blue"></div>
    <div class="hero-orb hero-orb-green"></div>

    <div class="legal-hero-inner">
      <span class="brand-eyebrow">Legal</span>
      <h1>Terms of Service</h1>
      <p class="brand-lede">
        The rules of the road for using Skip To My Loo. Plain language first, legalese where it counts.
      </p>
      <p class="last-updated">Last Updated: {{ lastUpdated }}</p>
    </div>
  </section>

  <!-- ─── Content ───────────────────────────────────────────────────── -->
  <section class="legal-body brand-section">
    <div class="legal-grid">

      <!-- Sticky nav -->
      <nav class="legal-toc" aria-label="Section navigation">
        <p class="toc-label">On this page</p>
        <ol class="toc-list">
          <li><a [routerLink]="['/terms']" fragment="acceptance">Acceptance</a></li>
          <li><a [routerLink]="['/terms']" fragment="description">Description</a></li>
          <li><a [routerLink]="['/terms']" fragment="accounts">Accounts &amp; eligibility</a></li>
          <li><a [routerLink]="['/terms']" fragment="conduct">Content &amp; conduct</a></li>
          <li><a [routerLink]="['/terms']" fragment="copyright">Copyright complaints</a></li>
          <li><a [routerLink]="['/terms']" fragment="premium">STML Premium</a></li>
          <li><a [routerLink]="['/terms']" fragment="app-stores">App store terms</a></li>
          <li><a [routerLink]="['/terms']" fragment="progression">Levels &amp; badges</a></li>
          <li><a [routerLink]="['/terms']" fragment="games">Games</a></li>
          <li><a [routerLink]="['/terms']" fragment="location">Location</a></li>
          <li><a [routerLink]="['/terms']" fragment="ip">Intellectual property</a></li>
          <li><a [routerLink]="['/terms']" fragment="third-party">Third-party content</a></li>
          <li><a [routerLink]="['/terms']" fragment="disclaimers">Disclaimers &amp; safety</a></li>
          <li><a [routerLink]="['/terms']" fragment="liability">Liability</a></li>
          <li><a [routerLink]="['/terms']" fragment="indemnification">Indemnification</a></li>
          <li><a [routerLink]="['/terms']" fragment="governing-law">Governing law &amp; disputes</a></li>
          <li><a [routerLink]="['/terms']" fragment="changes">Changes</a></li>
          <li><a [routerLink]="['/terms']" fragment="general">General terms</a></li>
          <li><a [routerLink]="['/terms']" fragment="contact">Contact</a></li>
        </ol>
      </nav>

      <!-- Body -->
      <div class="legal-content">

        <section id="acceptance" class="legal-section">
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing or using the Skip To My Loo mobile application and website (together, the
            "App"), you agree to be bound by these Terms of Service ("Terms") and our
            <a routerLink="/privacy">Privacy Policy</a>. If you do not agree to these Terms, do not use
            the App. JCC Development LLC ("we," "our," or "us") may modify these Terms as described
            in Section 17.
          </p>
          <p>
            <strong>Please read Section 16 carefully.</strong> It contains an agreement to resolve
            disputes through binding individual arbitration and a waiver of class actions and jury
            trials, to the extent permitted by law.
          </p>
        </section>

        <section id="description" class="legal-section">
          <h2>2. Description of Service</h2>
          <p>
            Skip To My Loo is a community-powered app that helps people locate public restrooms and
            includes a collection of retro arcade games. The App allows you to:
          </p>
          <ul>
            <li>Find and navigate to nearby restrooms</li>
            <li>Submit new restroom locations for the community</li>
            <li>Read and write reviews and ratings</li>
            <li>Save favorite restroom locations</li>
            <li>Play retro arcade games, track high scores, and compete on leaderboards</li>
            <li>Access additional features through an STML Premium subscription</li>
          </ul>
          <p>
            The free tier of the App is supported by advertising. Ads are served by Google AdMob
            and are configured as non-personalized, as described in our
            <a routerLink="/privacy">Privacy Policy</a>. STML Premium removes ads entirely.
          </p>
        </section>

        <section id="accounts" class="legal-section">
          <h2>3. Accounts and Eligibility</h2>

          <h3>Minimum Age</h3>
          <p>
            You must be at least 13 years old to create an account or use the App. If you are
            between 13 and the age of majority where you live, you may use the App only with the
            permission and supervision of a parent or legal guardian who agrees to these Terms on
            your behalf. We do not knowingly allow children under 13 to use the App.
          </p>

          <h3>Account Creation</h3>
          <p>
            To access certain features, you must create an account using an email address and
            password or by signing in with Apple or Google. You agree to provide accurate,
            current, and complete information and to keep it up to date.
          </p>

          <h3>Account Security</h3>
          <p>
            You are responsible for maintaining the confidentiality of your account credentials and
            for all activity that occurs under your account. Notify us right away at
            <a href="mailto:support@stml.app">support&#64;stml.app</a> if you believe your account
            has been accessed without your permission.
          </p>

          <h3>Deleting Your Account</h3>
          <p>
            You can permanently delete your account at any time from Settings in the App. Deleting
            your account removes your profile, reviews, favorites, badges, game scores, and pending
            submissions, as described in our <a routerLink="/privacy">Privacy Policy</a>. This cannot
            be undone.
          </p>

          <h3>Suspension and Termination</h3>
          <p>
            We may suspend or terminate your account if you violate these Terms, if we are required
            to by law, or if we reasonably believe your account poses a risk to the App or to other
            users. Where practical, we will tell you why.
          </p>
        </section>

        <section id="conduct" class="legal-section">
          <h2>4. User Content and Conduct</h2>

          <h3>Your Content</h3>
          <p>
            "User Content" means anything you submit to the App, including restroom locations and
            descriptions, reviews, ratings, and profile details. <strong>You keep ownership of your
            User Content.</strong> So that we can run the App, you grant JCC Development LLC a
            perpetual, irrevocable, worldwide, royalty-free, transferable, and sublicensable license
            to use, host, store, reproduce, modify, adapt, publish, translate, create derivative
            works from, distribute, and display your User Content in connection with operating,
            promoting, and improving the App. This license expressly includes the right to process,
            analyze, aggregate, and summarize your User Content using automated tools and
            artificial intelligence services (for example, generating a short summary of what
            reviewers say about a restroom, or checking a new submission against map data). It
            also lets us show your review to other users and keep restroom locations you
            contributed on the map after you delete your account.
          </p>
          <p>
            You represent that you have the rights needed to grant this license and that your User
            Content does not infringe anyone else's rights. Reviews and restroom submissions are
            public, so please do not include personal information about yourself or others.
          </p>

          <h3>Prohibited Conduct</h3>
          <p>You agree not to:</p>
          <ul>
            <li>Post false, misleading, or fraudulent reviews or locations</li>
            <li>Harass, abuse, threaten, or harm other users</li>
            <li>Post offensive, discriminatory, sexually explicit, or otherwise inappropriate content</li>
            <li>Post content that infringes a copyright, trademark, or other right of any person</li>
            <li>Post private information about any person without their consent</li>
            <li>Attempt to manipulate ratings, badges, XP, or leaderboards</li>
            <li>Use the App for any illegal purpose or in violation of any applicable law</li>
            <li>Reverse engineer, decompile, or disassemble the App, except where the law expressly allows it</li>
            <li>Use automated systems, scrapers, or bots to access the App</li>
            <li>Interfere with or disrupt the App, its servers, or its networks</li>
          </ul>
          <p>
            We may remove or edit User Content that violates these Terms, and we may moderate
            content with the help of automated tools and human reviewers.
          </p>
        </section>

        <section id="copyright" class="legal-section">
          <h2>5. Copyright Complaints (DMCA)</h2>
          <p>
            We respect the intellectual property of others and respond to notices of alleged
            copyright infringement that comply with the Digital Millennium Copyright Act ("DMCA").
            If you believe content in the App infringes your copyright, send a written notice to our
            designated agent that includes:
          </p>
          <ul>
            <li>Your physical or electronic signature</li>
            <li>Identification of the copyrighted work you claim has been infringed</li>
            <li>Identification of the material you claim is infringing and enough information for us to locate it (for example, the restroom name and the review text)</li>
            <li>Your contact information (name, address, telephone number, and email address)</li>
            <li>A statement that you have a good-faith belief that the use is not authorized by the copyright owner, its agent, or the law</li>
            <li>A statement, under penalty of perjury, that the information in your notice is accurate and that you are the copyright owner or are authorized to act on the owner's behalf</li>
          </ul>
          <div class="contact-block">
            <strong>Designated Copyright Agent, JCC Development LLC</strong>
            <a href="mailto:legal@stml.app">legal&#64;stml.app</a>
            <!-- TODO: add the LLC's mailing address here once confirmed. -->
          </div>
          <p>
            If you believe your content was removed by mistake, you may send us a counter-notice
            at the same address with the information required by 17 U.S.C. § 512(g)(3). We may
            terminate the accounts of repeat infringers.
          </p>
        </section>

        <section id="premium" class="legal-section">
          <h2>6. STML Premium</h2>
          <p>
            STML Premium is a paid, auto-renewing monthly subscription that unlocks additional
            features within the App. Subscriptions are purchased in the App and billed through
            your Apple App Store or Google Play account. Your subscription renews automatically
            each month unless you cancel at least 24 hours before the end of the current billing
            period. You can manage or cancel your subscription at any time in your device's
            subscription settings. After cancelling, you keep Premium until the end of the period
            you already paid for.
          </p>
          <p>
            Deleting your Skip To My Loo account does not cancel your subscription. Please cancel it
            in your App Store or Google Play subscription settings before or after deleting your
            account to avoid further charges.
          </p>
          <p>
            Refunds are handled by Apple or Google under their respective policies. If you have
            questions or need help with your subscription, contact us at
            <a href="mailto:support@stml.app">support&#64;stml.app</a>. We may change the features
            included in STML Premium or adjust subscription pricing with reasonable advance notice;
            price changes take effect at your next renewal after notice.
          </p>
        </section>

        <section id="app-stores" class="legal-section">
          <h2>7. App Store and Google Play Terms</h2>
          <p>
            If you downloaded the App from the Apple App Store or Google Play, the following
            additional terms apply, and the platform's own terms also apply to your use of the App.
          </p>
          <ul>
            <li>These Terms are between you and JCC Development LLC only, not with Apple Inc. ("Apple") or Google LLC ("Google"). JCC Development LLC, not Apple or Google, is solely responsible for the App and its content.</li>
            <li>Your license to use the App is limited to a non-transferable license to use the App on Apple or Google devices that you own or control, as permitted by the applicable platform usage rules.</li>
            <li>Apple and Google have no obligation to provide maintenance or support for the App.</li>
            <li>If the App fails to conform to any applicable warranty, you may notify Apple or Google and they may refund the purchase price, if any. To the maximum extent permitted by law, Apple and Google have no other warranty obligation for the App.</li>
            <li>JCC Development LLC, not Apple or Google, is responsible for addressing any claims relating to the App, including product liability claims, claims that the App fails to conform to legal or regulatory requirements, and consumer protection claims.</li>
            <li>In the event of a third-party claim that the App infringes intellectual property rights, JCC Development LLC, not Apple or Google, is responsible for the investigation, defense, settlement, and discharge of that claim.</li>
            <li>You represent that you are not located in a country subject to a U.S. government embargo or designated as a "terrorist supporting" country, and that you are not on any U.S. government list of prohibited or restricted parties.</li>
            <li>Apple and Google, and their subsidiaries, are third-party beneficiaries of these Terms and may enforce them against you.</li>
          </ul>
        </section>

        <section id="progression" class="legal-section">
          <h2>8. Levels and Badges</h2>

          <h3>XP and Levels</h3>
          <p>
            The App includes a progression system in which you earn experience points (XP) through
            contributions such as writing reviews and submitting restroom locations. XP accumulates
            toward ten named levels: Newbie, Scout, Explorer, Local Expert, Restroom Ranger,
            Community Guide, Loo Legend, Facility Master, Grand Custodian, and Loo Royalty.
          </p>
          <p>
            Levels are for recognition only. They do not unlock paid features or carry any
            real-world value. We may adjust XP thresholds, level names, and the criteria by which
            XP is awarded at any time.
          </p>

          <h3>Badges</h3>
          <p>
            Badges are awarded automatically when your account activity meets defined criteria
            (such as the number of reviews submitted or verified restroom locations contributed)
            or manually by our moderation team. Each badge is unique per account.
          </p>
          <p>
            Badges are cosmetic achievements. They carry no monetary value and cannot be
            transferred or sold. We may revoke badges that were earned through conduct that
            violates these Terms, and we may modify or retire badges at any time.
          </p>
        </section>

        <section id="games" class="legal-section">
          <h2>9. Games, Leaderboards, and Events</h2>
          <p>
            The App includes retro arcade games with high score tracking, leaderboards, and
            occasional tournaments or weekly challenges that may award badges or temporary
            Premium access. We may remove scores or results that we determine were achieved through
            cheating, manipulation, or exploitation of bugs, and we may change or end any event at
            any time. Event rewards have no cash value and cannot be exchanged or transferred.
          </p>
        </section>

        <section id="location" class="legal-section">
          <h2>10. Location Services</h2>
          <p>
            The App uses your device's location to show nearby restrooms. By enabling location
            services, you consent to the collection and use of your location data as described in
            our <a routerLink="/privacy">Privacy Policy</a>. You can disable location access at any
            time in your device settings, though this will limit what the App can do.
          </p>
        </section>

        <section id="ip" class="legal-section">
          <h2>11. Intellectual Property</h2>
          <p>
            The App and its original content, features, and functionality are owned by JCC
            Development LLC and are protected by copyright, trademark, and other intellectual
            property laws. "Skip To My Loo," "STML," and our logos are trademarks of JCC Development
            LLC.
          </p>
          <p>
            Subject to these Terms, we grant you a limited, non-exclusive, non-transferable,
            revocable license to use the App for your personal, non-commercial use. You may not
            copy, modify, distribute, sell, or lease any part of the App without our express written
            permission.
          </p>
        </section>

        <section id="third-party" class="legal-section">
          <h2>12. Third-Party Content and Services</h2>
          <p>
            The App displays content submitted by other users and content from third parties,
            including map data and restroom listings imported from public sources. The App also
            relies on third-party services such as Google Maps, Apple and Google sign-in, and the
            app stores. We do not endorse and are not responsible for third-party content or
            services, and your use of them may be subject to their own terms.
          </p>
        </section>

        <section id="disclaimers" class="legal-section">
          <h2>13. Disclaimers and Safety</h2>

          <h3>We Do Not Own or Control Listed Locations</h3>
          <p>
            JCC Development LLC does not own, operate, inspect, manage, or control any restroom or
            premises listed in the App. Listings are contributed by users or imported from public
            data sources and have not been independently verified by us. We cannot guarantee that
            any location exists, is open, is clean, is safe, is accessible, or complies with the
            Americans with Disabilities Act or any other accessibility, sanitation, or building
            standard. Hours, conditions, and availability can change without notice.
          </p>

          <h3>Driving and Navigation Safety</h3>
          <p>
            <strong>Never use or interact with the App while driving, cycling, or operating any
            vehicle or machinery.</strong> Set your destination before you start moving, and follow
            all traffic laws, posted signs, and road conditions. The App is not a substitute for
            your own judgment, and any directions it provides may be inaccurate or out of date.
          </p>

          <h3>No Safety Guarantee and Reliance at Your Own Risk</h3>
          <p>
            <strong>The App is an information tool only. It is not a safety service.</strong> By
            using the App to locate or travel to a restroom, you accept full responsibility for
            assessing the suitability and safety of any location before entering it. A location
            that a past reviewer found clean, safe, or accessible may have changed or may be unsafe
            when you visit. JCC Development LLC expressly disclaims any liability for personal
            injury, death, property damage, or any other harm arising from your decision to visit a
            location identified through the App.
          </p>

          <h3>No Warranty</h3>
          <p>
            THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER
            EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY,
            FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE
            APP WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE, OR THAT ANY INFORMATION IN THE APP IS
            ACCURATE OR COMPLETE. SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF IMPLIED WARRANTIES,
            SO SOME OF THE ABOVE EXCLUSIONS MAY NOT APPLY TO YOU.
          </p>
        </section>

        <section id="liability" class="legal-section">
          <h2>14. Limitation of Liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, JCC DEVELOPMENT LLC AND ITS MEMBERS,
            OFFICERS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
            SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUES, DATA, USE,
            GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR RELATING TO:
          </p>
          <ul>
            <li>Your use of or inability to use the App</li>
            <li>Any unauthorized access to or use of our servers or your information</li>
            <li>Any bugs, viruses, or other harmful code transmitted through the App</li>
            <li>Any errors, omissions, or inaccuracies in any content, whether user-submitted or otherwise</li>
            <li>Personal injury, death, or property damage resulting from visiting or relying on information about any location found through the App</li>
          </ul>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE TOTAL AGGREGATE LIABILITY OF JCC
            DEVELOPMENT LLC FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THE APP OR THESE TERMS SHALL
            NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID TO JCC DEVELOPMENT LLC FOR THE APP IN
            THE TWELVE (12) MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM, OR (B) ONE HUNDRED
            U.S. DOLLARS ($100).
          </p>
          <p>
            Some jurisdictions do not allow the exclusion or limitation of liability for
            consequential or incidental damages, or for death, personal injury, fraud, gross
            negligence, or intentional misconduct. In those jurisdictions, our liability is limited
            to the fullest extent permitted by law, and nothing in these Terms limits any liability
            that cannot lawfully be limited.
          </p>
        </section>

        <section id="indemnification" class="legal-section">
          <h2>15. Indemnification</h2>
          <p>
            You agree to defend, indemnify, and hold harmless JCC Development LLC and its members,
            officers, employees, and agents from and against any claims, damages, obligations,
            losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising
            from your use of the App, your User Content, or your violation of these Terms or of any
            law or third-party right.
          </p>
        </section>

        <section id="governing-law" class="legal-section">
          <h2>16. Governing Law and Dispute Resolution</h2>

          <h3>Governing Law</h3>
          <p>
            These Terms and any dispute arising out of or relating to them or the App are governed
            by the laws of the State of Indiana and the federal laws of the United States, without
            regard to conflict of law principles. The United Nations Convention on Contracts for the
            International Sale of Goods does not apply.
          </p>

          <h3>Informal Resolution First</h3>
          <p>
            Most concerns can be solved quickly. Before starting a formal proceeding, you agree to
            contact us at <a href="mailto:legal@stml.app">legal&#64;stml.app</a> with a description
            of the issue and give us 30 days to try to resolve it with you.
          </p>

          <h3>Binding Arbitration</h3>
          <p>
            If we cannot resolve a dispute informally, you and JCC Development LLC agree that any
            dispute, claim, or controversy arising out of or relating to these Terms or the App
            will be resolved by binding individual arbitration administered by the American
            Arbitration Association ("AAA") under its Consumer Arbitration Rules, rather than in
            court, except that either party may bring an individual claim in small claims court.
            The Federal Arbitration Act governs this agreement to arbitrate. The arbitrator may
            award the same relief a court could award to you individually. Arbitration may be
            conducted by video, phone, or written submissions, or in Indiana if an in-person
            hearing is required.
          </p>

          <h3>Class Action and Jury Trial Waiver</h3>
          <p>
            <strong>To the fullest extent permitted by law, you and JCC Development LLC each agree
            that disputes will be resolved only on an individual basis and not as a plaintiff or
            class member in any purported class, collective, or representative proceeding, and each
            of us waives any right to a jury trial.</strong> If this waiver is found unenforceable
            as to a particular claim, that claim will proceed in court as described below rather
            than in arbitration.
          </p>

          <h3>Opt-Out</h3>
          <p>
            You may opt out of this arbitration agreement by emailing
            <a href="mailto:legal@stml.app">legal&#64;stml.app</a> within 30 days of first
            accepting these Terms, stating that you wish to opt out of arbitration. Opting out does
            not affect any other part of these Terms.
          </p>

          <h3>Venue</h3>
          <p>
            For any dispute that is not subject to arbitration, you and JCC Development LLC agree to
            the exclusive jurisdiction and venue of the state and federal courts located in
            Indiana, and you consent to personal jurisdiction in those courts.
          </p>
        </section>

        <section id="changes" class="legal-section">
          <h2>17. Changes to These Terms</h2>
          <p>
            We may modify these Terms from time to time. If we make material changes, we will post
            the updated Terms on this page, update the "Last Updated" date, and, where appropriate,
            notify you in the App or by email. Your continued use of the App after changes take
            effect constitutes acceptance of the new Terms. If you do not agree, stop using the App
            and delete your account.
          </p>
        </section>

        <section id="general" class="legal-section">
          <h2>18. General Terms</h2>

          <h3>Severability</h3>
          <p>
            If any provision of these Terms is found to be unenforceable or invalid, that provision
            will be limited or eliminated to the minimum extent necessary so that the remaining
            Terms stay in full force and effect.
          </p>

          <h3>No Waiver</h3>
          <p>
            Our failure to enforce any right or provision of these Terms is not a waiver of that
            right or provision.
          </p>

          <h3>Assignment</h3>
          <p>
            You may not assign or transfer these Terms without our prior written consent. We may
            assign these Terms in connection with a merger, acquisition, or sale of assets.
          </p>

          <h3>Entire Agreement</h3>
          <p>
            These Terms, together with our Privacy Policy and Cookie Policy, constitute the entire
            agreement between you and JCC Development LLC regarding the App and supersede all prior
            agreements and understandings.
          </p>
        </section>

        <section id="contact" class="legal-section">
          <h2>19. Contact Information</h2>
          <p>If you have questions about these Terms, please contact us at:</p>
          <div class="contact-block">
            <strong>JCC Development LLC</strong>
            <a href="mailto:legal@stml.app">legal&#64;stml.app</a>
            <a routerLink="/contact">Contact form</a>
            <!-- TODO: add the LLC's mailing address here once confirmed. -->
          </div>
        </section>

      </div>
    </div>
  </section>
</main>

<app-footer></app-footer>
